/**
 * The engine mowers' running sound, built as one loop of exhaust pulses ("brrrr"): a small engine
 * is a train of thumps 20-35 times a second, and the ear hears that rate as the rumble while the
 * thumps themselves sit at 150-600 Hz, which phone speakers can actually play. Pulses are slightly
 * irregular in timing and strength so the loop sounds alive, like a lip trill, not a buzzer.
 *
 * Pure (no Web Audio) so it can be tested and rendered offline.
 */

export interface EngineProfile {
  /** Exhaust pulses per second at the mower's normal speed. */
  firingHz: number;
  /** Resonance of each thump; lower is deeper. */
  bodyHz: number;
  /** Seconds for a thump to die away; longer is boomier. */
  decay: number;
  /** 0-1: share of crackle (noise) in each thump. */
  grit: number;
  /** 0-1: timing and strength wobble between pulses. */
  roughness: number;
  /** Every other pulse is this much weaker (0 = even); twin-cylinder lope. */
  lope: number;
}

export const ENGINE_PROFILES = {
  /** Small single-cylinder push mower: classic putt-putt. */
  push: { firingHz: 27, bodyHz: 150, decay: 0.011, grit: 0.35, roughness: 0.6, lope: 0 },
  /** Same family, a touch heavier. */
  selfPropelled: { firingHz: 25, bodyHz: 138, decay: 0.012, grit: 0.3, roughness: 0.5, lope: 0 },
  /** Big, slow, deep. */
  tractor: { firingHz: 19, bodyHz: 112, decay: 0.016, grit: 0.25, roughness: 0.45, lope: 0.25 },
  /** Twin-cylinder: quicker, smoother, with a lope. */
  zeroTurn: { firingHz: 32, bodyHz: 132, decay: 0.01, grit: 0.25, roughness: 0.35, lope: 0.35 },
} as const satisfies Record<string, EngineProfile>;

/** Pulses in one loop: long enough that the wobble does not audibly repeat. */
const PULSES = 48;

/**
 * One seamless loop of the engine at its normal speed, peak-normalized to 1. Play it looping and
 * scale `playbackRate` with speed: the rumble and the thump pitch rise together, like a real engine.
 */
export function renderEngineLoop(
  sampleRate: number,
  p: EngineProfile,
  seed = 1,
): Float32Array<ArrayBuffer> {
  const rand = mulberry32(seed);
  const period = 1 / p.firingHz;
  const length = Math.round(PULSES * period * sampleRate);
  const out = new Float32Array(length);
  const tail = Math.min(length, Math.ceil(p.decay * 8 * sampleRate));

  for (let k = 0; k < PULSES; k++) {
    const jitter = (rand() * 2 - 1) * 0.05 * p.roughness * period;
    const start = (k * period + jitter) * sampleRate;
    const strength =
      (1 - (k % 2) * p.lope) * (1 - p.roughness * 0.35 * rand()) * (rand() < 0.04 ? 0.55 : 1);
    const body = p.bodyHz * (1 + (rand() * 2 - 1) * 0.04 * p.roughness);
    const phase = rand() * 0.6;
    let crackle = 0;
    for (let i = 0; i < tail; i++) {
      const t = i / sampleRate;
      const attack = 1 - Math.exp(-t / 0.0008);
      // Low-passed noise for the crackle (one-pole smoothing).
      crackle += (rand() * 2 - 1 - crackle) * 0.35;
      const v =
        Math.sin(2 * Math.PI * body * t) * Math.exp(-t / p.decay) +
        Math.sin(2 * Math.PI * body * 2.3 * t + phase) * Math.exp(-t / (p.decay * 0.7)) +
        0.55 * Math.sin(2 * Math.PI * body * 4.1 * t) * Math.exp(-t / (p.decay * 0.5)) +
        p.grit * 2.2 * crackle * Math.exp(-t / (p.decay * 0.4));
      // Wrap around so the loop joins seamlessly.
      const idx = (Math.floor(start) + i + length) % length;
      out[idx] = (out[idx] ?? 0) + attack * strength * v;
    }
  }

  // Remove any DC offset, then normalize.
  let mean = 0;
  for (const v of out) mean += v;
  mean /= length;
  let peak = 0;
  for (let i = 0; i < length; i++) {
    out[i] = (out[i] ?? 0) - mean;
    peak = Math.max(peak, Math.abs(out[i] ?? 0));
  }
  if (peak > 0) for (let i = 0; i < length; i++) out[i] = (out[i] ?? 0) / peak;
  return out;
}

/** RMS of a signal (for level balancing and tests). */
export function rms(samples: Float32Array): number {
  let sum = 0;
  for (const v of samples) sum += v * v;
  return Math.sqrt(sum / samples.length);
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
